import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from '../RouterCompatibility';
import classNames from 'classnames';
import { slide as Menu } from 'react-burger-menu';
import Locale from '../Locale';
import { withTranslation } from '../ChildrenContext';
import closeReactWidgets from '../../utils/closeReactWidgets';
import { useTemplateConfig } from '../../context/TemplateConfigContext';
import runtimeConfig from '../../config/runtimeConfig';
import store from '../../stores/redux';

// Nav items use item.href directly from template navigation config.
// For items without an explicit href (e.g. the 'home' root entry),
// fall back to the configured default route.
const resolveHref = (item) => item.href || runtimeConfig.defaultRoute || '/';

// Nav icons are template-owned — each skin has SVGs with skin-specific stroke/fill colors.
// They are provided via assets.navIcons from the active template config.

const CLICKABLE_OVERLAY_SELECTOR = '.dropdown-toggle, .rw-widget, .top-header__user, .locale, button, a, [role="button"], input, select, textarea, label';

const getClickableElement = (target) => {
    if (!target) {
        return null;
    }

    return target.closest(CLICKABLE_OVERLAY_SELECTOR) || target;
};

const dispatchClick = (element) => {
    if (!element) {
        return;
    }

    if (typeof element.click === 'function') {
        element.click();
        return;
    }

    const clickEvent = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window
    });

    element.dispatchEvent(clickEvent);
};

const shouldForwardOverlayClick = (target) => {
    if (!target) {
        return false;
    }

    if (target.closest('.bm-menu') || target.closest('.sidebar')) {
        return false;
    }

    if (target.closest('.top-header__menu-toggle')) {
        return false;
    }

    return true;
};

const SideBar = ({
    activeItemIndex,
    openSubmenu,
    isNavExtended,
    toggleNav,
    location,
    t
}) => {
    // Navigation and Template Assets are sourced from template config — not from legacy config.js
    const { navigation, assets } = useTemplateConfig();
    const list = navigation?.sidebar || [];
    const navIcons = assets?.navIcons || {};

    const [state, setState] = useState({
        locale: store.getState().user.locale,
        activeItemIndex: activeItemIndex,
        openSubmenu: openSubmenu,
    });

    const isTogglingRef = useRef(false);
    const overlayClickTargetRef = useRef(null);
    const staticNames = (t && t.sidebar) || {};

    const isParentActive = useCallback((item) => {
        const currentPath = location || '';
        const parentHref = resolveHref(item);

        if (currentPath.startsWith('/offers/offer/') && parentHref === '/offers/goals') {
            return true;
        }

        if (currentPath === parentHref || currentPath.startsWith(parentHref + '/')) {
            return true;
        }

        if (Array.isArray(item.subMenu) && item.subMenu.length) {
            return item.subMenu.some(sub => {
                const childHref = sub.href;
                if (currentPath === childHref || currentPath.startsWith(childHref + '/')) {
                    return true;
                }
                // /statistics/conversion/:id belongs to the conversions sub-item
                if (childHref === '/statistics/conversions' && currentPath.startsWith('/statistics/conversion/')) {
                    return true;
                }
                return false;
            });
        }

        return false;
    }, [location]);

    const getActiveIndex = useCallback(() => {
        const currentPath = location || '';
        let activeIdx = -1;

        (list || []).forEach((item, i) => {
            const parentHref = resolveHref(item);

            if (currentPath.startsWith('/offers/offer/') && parentHref === '/offers/goals') {
                activeIdx = i;
                return;
            }

            if (currentPath === parentHref || currentPath.startsWith(parentHref + '/')) {
                activeIdx = i;
                return;
            }

            if (Array.isArray(item.subMenu) && item.subMenu.length) {
                const childMatch = item.subMenu.some(sub => {
                    const childHref = sub.href;
                    if (currentPath === childHref || currentPath.startsWith(childHref + '/')) {
                        return true;
                    }
                    if (childHref === '/statistics/conversions' && currentPath.startsWith('/statistics/conversion/')) {
                        return true;
                    }
                    return false;
                });
                if (childMatch) {
                    activeIdx = i;
                }
            }
        });
        return activeIdx;
    }, [list, location]);

    const hasActiveSubItem = useCallback((item) => {
        if (!Array.isArray(item?.subMenu) || !item.subMenu.length) {
            return false;
        }

        const currentPath = location || '';

        return item.subMenu.some(sub => {
            const childHref = sub.href;
            if (currentPath === childHref || currentPath.startsWith(childHref + '/')) {
                return true;
            }
            if (childHref === '/statistics/conversions' && currentPath.startsWith('/statistics/conversion/')) {
                return true;
            }
            if (childHref === '/postback/list' && (currentPath.startsWith('/postback/edit/') || currentPath === '/postback/create')) {
                return true;
            }
            return false;
        });
    }, [location]);

    const isMatchPath = useCallback((data) => {
        const params = {
            exact: typeof data.exact !== "undefined" ? data.exact : false,
            href: resolveHref(data),
            ...data
        };
        const currentPath = location;

        if (params.exact) {
            return currentPath === params.href;
        } else {
            return currentPath === params.href || currentPath.startsWith((params.href || '') + '/');
        }
    }, [location]);

    const getSubMenuClass = useCallback((item) => {
        const href = item.href;
        const exact = typeof item.exact !== 'undefined' ? item.exact : true;
        const cur = location || '';
        let active = exact ? (cur === href) : (cur === href || cur.startsWith(href + '/'));
        if (!active && href === '/statistics/conversions' && cur.startsWith('/statistics/conversion/')) {
            active = true;
        }
        if (!active && href === '/postback/list' && (cur.startsWith('/postback/edit/') || cur === '/postback/create')) {
            active = true;
        }
        return active ? 'nav__sub-menu--active' : ''
    }, [location]);

    const getGroupClassName = useCallback((active) => {
        return classNames({
            'active': active,
            'opened': active && state.openSubmenu
        })
    }, [state.openSubmenu]);

    const getClassMain = useCallback(() => {
        return classNames({
            'sidebar': true,
            'opened': isNavExtended,
            'sidebar--extened': isNavExtended,
        });
    }, [isNavExtended]);

    const runAfterWidgetsClose = useCallback((fn) => {
        const closedWidgets = closeReactWidgets();
        if (closedWidgets) {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    fn();
                });
            });
        } else {
            fn();
        }
    }, []);

    const onCloseSidebar = useCallback(() => {
        runAfterWidgetsClose(() => {
            const overlay = document.querySelector('.bm-overlay');
            if (overlay) {
                overlay.click();
            }
        });
    }, [runAfterWidgetsClose]);

    const onClickLink = useCallback(() => {
        runAfterWidgetsClose(() => {
            toggleNav(false);
        });
    }, [toggleNav, runAfterWidgetsClose]);

    const onCloseMenu = useCallback(() => {
        runAfterWidgetsClose(() => {
            toggleNav(false);
        });
    }, [toggleNav, runAfterWidgetsClose]);

    const onClickItem = useCallback((event) => {
        const elem = event.target.closest('.nav__tab');
        if (!elem) return;

        const index = +elem.getAttribute('data-index');
        const isLink = elem.getAttribute('data-link');

        onCloseMenu();

        setState(prevState => ({
            ...prevState,
            activeItemIndex: index,
            openSubmenu: prevState.activeItemIndex !== index ? true : !prevState.openSubmenu
        }));

        if(isLink) onClickLink();
    }, [onCloseMenu, onClickLink]);

    const onToggleNav = useCallback(() => {
        if (isTogglingRef.current) {
            return;
        }
        isTogglingRef.current = true;

        runAfterWidgetsClose(() => {
            toggleNav(!isNavExtended);
            setTimeout(() => {
                isTogglingRef.current = false;
            }, 300);
        });
    }, [toggleNav, isNavExtended, runAfterWidgetsClose]);

    useEffect(() => {
        setState(prevState => ({
            ...prevState,
            activeItemIndex: getActiveIndex()
        }));
    }, [getActiveIndex]);

    useEffect(() => {
        if (location) {
            const nextActiveItemIndex = getActiveIndex();
            const nextActiveItem = list[nextActiveItemIndex];
            const shouldOpenSubmenu = hasActiveSubItem(nextActiveItem);

            setState(prevState => ({
                ...prevState,
                activeItemIndex: nextActiveItemIndex,
                openSubmenu: shouldOpenSubmenu
                    ? true
                    : prevState.activeItemIndex !== nextActiveItemIndex
                        ? false
                        : prevState.openSubmenu
            }));
        }
    }, [location, getActiveIndex, hasActiveSubItem, list]);

    // Close sidebar on route change
    useEffect(() => {
        if (isNavExtended) {
            toggleNav(false);
        }
    }, [location]);

    useEffect(() => {
        if (!isNavExtended) {
            return;
        }

        const handleOverlayClick = (event) => {
            const overlay = event.target.closest('.bm-overlay');
            if (!overlay) {
                return;
            }

            overlay.style.pointerEvents = 'none';
            const actualTarget = document.elementFromPoint(
                event.clientX,
                event.clientY
            );
            overlay.style.pointerEvents = '';

            const isInsideMenu = actualTarget && (actualTarget.closest('.bm-menu') || actualTarget.closest('.sidebar'));

            if (isInsideMenu) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            const shouldForwardClick = shouldForwardOverlayClick(actualTarget);
            overlayClickTargetRef.current = shouldForwardClick ? actualTarget : null;

            toggleNav(false);

            if (shouldForwardClick) {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        if (!overlayClickTargetRef.current) {
                            return;
                        }

                        const targetElement = getClickableElement(overlayClickTargetRef.current);
                        overlayClickTargetRef.current = null;

                        if (targetElement) {
                            dispatchClick(targetElement);
                        }
                    });
                });
            }
        };

        const overlay = document.querySelector('.bm-overlay');
        if (overlay) {
            overlay.addEventListener('click', handleOverlayClick, true);
            return () => {
                overlay.removeEventListener('click', handleOverlayClick, true);
            };
        }
    }, [isNavExtended, toggleNav]);

    const dashboardPath = runtimeConfig.defaultRoute || '/';

    return (
        <Menu
            isOpen={isNavExtended}
            onStateChange={(state) => {
                if (state.isOpen !== isNavExtended && !isTogglingRef.current) {
                    toggleNav(state.isOpen);
                }
            }}
            pageWrapId={"page-wrapper"}
            outerContainerId={"wrapper"}
        >
            <div className="sidebar__logo_wrapper">
                <div className="sidebar__logo_wrapper__inner">
                    <Link
                        className="sidebar__logo"
                        to={dashboardPath}
                        data-link={true}
                        onClick={() => onCloseMenu()}
                    >
                        <img src={isNavExtended ? assets.brand.logoExtended : assets.brand.logo} alt=""/>
                    </Link>
                    <div onClick={() => onCloseMenu()} className="sidebar__close">
                        <img src={assets.icons.cross} alt=""/>
                    </div>
                </div>
            </div>

            <nav className="nav">
                <ul>
                    {list.map((item, i) => {
                        let isActive = isParentActive(item);
                        return (
                            <li className={getGroupClassName(isActive)} key={i}>
                                {item.isNativeLink ? (
                                    <a className="nav__tab has-sub" href={item.href} target={item.target}>
                                        <span
                                            className="nav-label">{staticNames[item.nameKey || item.key]}</span>
                                    </a>
                                ) : (
                                    <Link
                                        className="nav__tab has-sub"
                                        to={resolveHref(item)}
                                        data-index={i}
                                        data-link={true}
                                        onClick={onClickItem}
                                    >
                                        {item.icon && <img src={navIcons[item.icon]} alt={navIcons[item.icon]}/>}
                                        {isNavExtended
                                            ? <span
                                                className="nav-label bold">{staticNames[item.nameKey || item.key]}</span>
                                            : null
                                        }
                                    </Link>
                                )
                                }
                                {item.subMenu && isNavExtended
                                    ? <ul className="nav__sub-menu">
                                        {item.subMenu.map(subItem => (
                                            <li key={subItem.key} className={getSubMenuClass(subItem)}>
                                                <Link
                                                    className="nav__tab"
                                                    to={subItem.href}
                                                    data-index={i}
                                                    data-link={true}
                                                    onClick={onClickItem}
                                                >
                                                    <span
                                                        className="nav-label">{staticNames[subItem.nameKey || subItem.key]}</span>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                    : null
                                }
                            </li>
                        )
                    })}
                    <li className="nav__toggle">
                        <button className="nav__tab" onClick={onToggleNav}>
                            <img className={isNavExtended ? '' : 'reverse'} src={navIcons.arrowNav}
                                 alt=""/>
                            {isNavExtended ? staticNames.collapse : null}
                        </button>
                    </li>
                </ul>
            </nav>
            <Locale />
        </Menu>
    );
};

SideBar.propTypes = {
    activeItemIndex: PropTypes.number,
    openSubmenu: PropTypes.bool,
    isNavExtended: PropTypes.bool,
    toggleNav: PropTypes.func,
    location: PropTypes.string,
    t: PropTypes.object
};

SideBar.defaultProps = {
    activeItemIndex: 0,
    openSubmenu: true,
    isNavExtended: false,
    toggleNav: () => {},
    location: ''
};

export default withTranslation(SideBar);
